import { EditOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Spin, Table, Tag } from 'antd';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../context/AuthProvider';
import { updatePropsMyHistoryMentenancePlannigs } from '../../../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import { setMyHistoryMentenancePlannings } from '../../../../../contextRedux/MentenancePlanning/MentenancePlanningStore';
import MentenancePlanningService from '../../../../../services/mentenancePlanning/MentenancePlanningService';
import { RootState } from '../../../../../store';
import MentenancePlanningModal from '../../MentenancePlanningModal/MentenancePlanningModal';
const HistoryMentenancePlanning: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();

  const { propsMyHistoryMentenancePlannigs: planningProps } = useSelector(
    (state: RootState) => state.models.mentenancePlanning
  );
  const mentenancePlannings = useSelector(
    (state: RootState) => state.store.mentenancePlanning.myHistoryMentenancePlanningData ?? []
  );

  const { searchTerm, skip, take, refresh } = planningProps;
  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  const currentPage = useMemo(() => Math.floor(skip / take) + 1, [skip, take]);
  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const pageSizes = [10, 20, 30, 50];

  /** Helper function to dispatch multiple field updates */
  const updatePlanningProps = useCallback(
    (updates: Record<string, any>) => {
      Object.entries(updates).forEach(([field, value]) =>
        dispatch(updatePropsMyHistoryMentenancePlannigs({ field, value }))
      );
    },
    [dispatch]
  );

  /** Sync state with URL params */
  useEffect(() => {
    const queryParams = queryString.parse(location.search);
    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    updatePlanningProps({ searchTerm: termInUrl, skip: calculatedSkip, take: pageSizeInUrl });
    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };

    setIsSearchTermInitialized(true);
  }, [location.search, take, updatePlanningProps]);

  /** Update URL when search term or pagination changes */
  useEffect(() => {
    if (!isSearchTermInitialized) return;

    const { term, page, pageSize } = previousParamsRef.current;
    if (searchTerm !== term || currentPage.toString() !== page || take.toString() !== pageSize) {
      const params = {
        term: searchTerm || undefined,
        page: currentPage > 1 ? currentPage.toString() : undefined,
        pageSize: take !== 10 ? take.toString() : undefined,
      };

      navigate(`${location.pathname}?${queryString.stringify(params)}`, { replace: true });
      previousParamsRef.current = { term: searchTerm || '', page: currentPage.toString(), pageSize: take.toString() };
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname]);

  /** Fetch maintenance planning data */
  const { isFetching: isLoading } = useQuery({
    queryKey: ['mentenancePlannings', searchTerm, skip, take, refresh],
    queryFn: async () => {
      const data = await MentenancePlanningService.searchMyHistoryMentenancePlanning({
        assignedTechnicianId: profile?.id,
        isMyHistoryMentenancePlannings: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractIncluded: true,
        isWorkPlaceIncluded: true,
        isAssignedTechnicianIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        isInterventionFileIncluded: true,
        termen: searchTerm || undefined,
        skip,
        take,
      });

      updatePlanningProps({ totalItems: data?.totalItems || 0, refresh: false });
      dispatch(setMyHistoryMentenancePlannings(data?.response));
      return data?.response;
    },
    enabled: !!profile?.id,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  /** Debounced search handler */
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      updatePlanningProps({ searchTerm: value, skip: 0 });
    }, 500),
    [updatePlanningProps]
  );

  /** Cleanup debounce */
  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleSearch = (value: string) => debouncedSearch(value);
  const handleViewInfo = (record: any) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };
  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const baseColumns: any = [
    { title: 'Numar', dataIndex: 'number', render: (number: any) => <Tag color='blue'>{number}</Tag> },
    {
      title: 'Beneficiar',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
    },
    {
      title: 'Punct',
      dataIndex: '',
      render: (record: any) => {
        return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
      },
    },
    {
      title: 'Sistem',
      dataIndex: 'mentenancePlanningSystemTypes',
      render: (types: any[]) => types?.map((t: any) => `[${t.systemType.name}]`).join(' ') || '-',
    },
    {
      title: 'Perioada programata',
      dataIndex: 'appointment',
      render: (value: any) => moment(value).format('DD-MM-YYYY') || '-',
    },
    {
      title: 'Numar Contract',
      dataIndex: 'contract',
      render: (contract: any) => {
        return <>{contract?.number}</>;
      },
    },
    { title: 'Status', dataIndex: ['status', 'name'] },
    {
      title: 'Actiuni',
      render: (_: any, record: any) => <Button onClick={() => handleViewInfo(record)} icon={<EditOutlined />} />,
      align: 'center',
    },
  ];

  const paginationConfig = useMemo(
    () => ({
      current: currentPage,
      pageSize: take,
      total: planningProps.totalItems,
      showSizeChanger: true,
      pageSizeOptions: pageSizes.map(String),
      onChange: (page: any, size: any) => updatePlanningProps({ skip: (page - 1) * size, take: size }),
      onShowSizeChange: (_current: any, size: any) => updatePlanningProps({ skip: 0, take: size }),
    }),
    [currentPage, take, planningProps.totalItems, pageSizes, updatePlanningProps]
  );

  if (isLoading) return <Spin className='spinner' size='large' />;

  return (
    <>
      <Input.Search
        placeholder='Search...'
        defaultValue={searchTerm}
        onChange={(e) => handleSearch(e.target.value)}
        style={{ marginBottom: 10 }}
        autoFocus
      />
      <Table
        columns={baseColumns}
        dataSource={mentenancePlannings}
        pagination={paginationConfig}
        rowKey='number'
        scroll={{ x: 'max-content' }}
      />
      <MentenancePlanningModal
        isModalVisible={isModalVisible}
        handleModalClose={handleModalClose}
        selectedRecord={selectedRecord}
      />
    </>
  );
};

export default HistoryMentenancePlanning;
